import React, { useState } from "react";
import { Loader2, Mail, AlertTriangle, Check, Pencil, RotateCw } from "lucide-react";
import { api } from "@/lib/api";

function DestBadge({ destInfo }) {
  if (destInfo?.verified) {
    return <span className="inline-flex items-center gap-1 text-[11px] mono text-emerald-400" data-testid="email-dest-verified-badge"><Check size={11} /> verified</span>;
  }
  if (destInfo?.on_cloudflare) {
    return <span className="inline-flex items-center gap-1 text-[11px] mono text-amber-400" data-testid="email-dest-pending-badge"><AlertTriangle size={11} /> pending verification</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[11px] mono text-red-400" data-testid="email-dest-notcf-badge"><AlertTriangle size={11} /> not on Cloudflare</span>;
}

function DestEditor({ draft, setDraft, busy, hasCurrent, onSave, onCancel }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input
          type="email" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="you@example.com" data-testid="email-dest-input" disabled={busy} autoFocus
          className="admin-input flex-1 min-w-[16rem] bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm font-mono outline-none focus:border-amber-500 disabled:opacity-50"
        />
        <button onClick={onSave} disabled={busy || !draft} data-testid="email-dest-save-btn" className="inline-flex items-center gap-2 bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-4 py-2 rounded-sm text-sm font-semibold">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Save &amp; verify
        </button>
        {hasCurrent && <button onClick={onCancel} disabled={busy} className="text-zinc-400 hover:text-white px-3 py-2 text-sm">Cancel</button>}
      </div>
      <div className="text-[11px] text-zinc-500 leading-relaxed">
        Cloudflare will send a verification email to this address. <b>Emails will keep bouncing until you click that verify link</b> — CF refuses to forward to unverified destinations.
      </div>
    </div>
  );
}

function DestCurrent({ adminDest, destInfo, busy, onResend }) {
  return (
    <>
      <div className="font-display text-2xl mono flex items-center gap-3" data-testid="email-dest-current"><Mail size={18} className="text-amber-500" />{adminDest}</div>
      {destInfo?.auto_synced && <div className="mt-2 text-[10px] mono text-emerald-400/80 border border-emerald-500/20 bg-emerald-500/5 rounded-sm px-2 py-1 inline-block">Auto-detected from Cloudflare rules and saved to the database.</div>}
      {destInfo?.source === "environment" && <div className="mt-2 text-[10px] mono text-zinc-500 opacity-70">(from environment variable — saving from this UI will persist it in the database instead)</div>}
      {!destInfo?.verified && destInfo?.on_cloudflare && (
        <button onClick={onResend} disabled={busy} data-testid="email-dest-resend-btn" title="Cloudflare only sends the verify email once. This button re-checks status and shows next steps."
          className="mt-3 inline-flex items-center gap-1 text-[11px] mono text-amber-300 hover:text-amber-100 border border-amber-500/40 hover:border-amber-300 rounded-sm px-2.5 py-1 disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCw size={11} />} Check verification status
        </button>
      )}
    </>
  );
}

/** "Forward all incoming email to" card — editable admin destination with verification state. */
export default function AdminDestinationPanel({ destInfo, adminDest, refresh, setErr }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  async function call(fn, fallback) {
    setBusy(true); setErr(""); setMessage("");
    try { const r = await fn(); setMessage(r.data.message || fallback); refresh(); return true; }
    catch (e) { setErr(e.response?.data?.detail || e.message); return false; }
    finally { setBusy(false); }
  }
  async function save() {
    const email = draft.trim().toLowerCase();
    if (!email || !email.includes("@")) { setErr("Enter a valid email address"); return; }
    if (await call(() => api.put(`/email/admin-destination`, { email }), "Destination updated.")) setEditing(false);
  }
  const resend = () => call(() => api.post(`/email/admin-destination/resend-verification`), "Status checked.");
  function beginEdit() { setDraft(destInfo?.email || ""); setEditing(true); setMessage(""); }

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5" data-testid="email-dest-panel">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="label-tiny">Forward all incoming email to</div>
        {adminDest && !editing && (
          <div className="flex items-center gap-2">
            <DestBadge destInfo={destInfo} />
            <button onClick={beginEdit} disabled={busy} data-testid="email-dest-edit-btn" className="inline-flex items-center gap-1 text-[11px] mono text-zinc-400 hover:text-white border border-zinc-700 hover:border-white rounded-sm px-2 py-1 disabled:opacity-50"><Pencil size={11} /> Change</button>
          </div>
        )}
      </div>
      {editing || !adminDest
        ? <DestEditor draft={draft} setDraft={setDraft} busy={busy} hasCurrent={!!adminDest} onSave={save} onCancel={() => { setEditing(false); setMessage(""); }} />
        : <DestCurrent adminDest={adminDest} destInfo={destInfo} busy={busy} onResend={resend} />}
      {message && <div className="mt-3 text-xs mono text-emerald-300 border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-2.5" data-testid="email-dest-success">{message}</div>}
    </div>
  );
}
